import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Checkbox, Input, message, Space, Upload } from "antd";
import api from "../../utils/axios";
import CustomInput from "../../components/form/CustomInput";
import useLang from "../../hooks/useLang";
import { toast } from "react-toastify";
import { getUserRoleFromToken } from "../../utils/Auth";
import { userRoles } from "../../types/enums";
import { CopyOutlined } from "@ant-design/icons";
import "./MyAccount.css";
import DefaultPasswordInput from "../../components/form/DefaultPasswordInput";
import { usernamePasswordValidate } from "../../types/UsernamePasswordValidate";

const MyAccount = () => {
	const [formData, setFormData] = useState({
		id: undefined,
		nameAndSurname: "",
		businessName: "",
		businessNumber: "",
		email: "",
		username: "",
		phone: "",
		address: "",
		country: "",
		city: "",
		oldPassword: "",
		newPassword: "",
		confirmPassword: "",
		showNumberInOrder: false,
		digitalDescription: "",
		serviceCostKs: "",
		serviceCostMk: "",
		serviceCostAl: "",
	});
	const [loading, setLoading] = useState<boolean>(false);
	const [userRole, setUserRole] = useState<any>();
	const [token, setToken] = useState<string | null>("");
	const [error, setError] = useState<boolean>(false);

	const { strings } = useLang("myaccount");

	const changeShowNumberInOrder = () => {
		setFormData((prevState) => ({
			...prevState,
			showNumberInOrder: !prevState.showNumberInOrder,
		}));
	};

	const clearData = () => {
		setFormData((prevState) => ({
			...prevState,
			oldPassword: "",
			newPassword: "",
			confirmPassword: "",
			digitalDescription: "",
			serviceCostKs: "",
			serviceCostMk: "",
			serviceCostAl: "",
		}));
		getUserById();
	};
	const getUserById = async () => {
		setLoading(true);
		try {
			const response = await api.get("User/userbyid");
			if (response.data) {
				setFormData(response.data);
			}
		} catch (error) {
			console.log(error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		const userRoleId = getUserRoleFromToken();
		setUserRole(userRoleId);

		if (
			userRoleId === userRoles.CLIENT ||
			userRoleId === userRoles.SUBCLIENT
		) {
			const selectToken = async () => {
				try {
					const response = await api.get("User/token");
					if (response.status === 200) {
						const result = response.data;
						setToken(result);
					}
				} catch (error) {
					console.log(error);
				}
			};
			selectToken();
		}

		getUserById();
	}, []);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData((prevState) => ({
			...prevState,
			[name]: value,
		}));
	};

	const updateProfile = async () => {
		if (error) {
			toast.error("New password and confirm password do not match!");
			return;
		}

		if (
			formData.newPassword?.length > 0 &&
			formData.confirmPassword?.length > 0
		) {
			const validation = usernamePasswordValidate(
				formData.username,
				formData.newPassword
			);

			if (!validation.valid) {
				toast.error(validation.message);
				return;
			}
		}

		const data = {
			id: formData.id,
			username: formData.username,
			showNumberInOrder: formData.showNumberInOrder,
			newPassword: formData.newPassword || null, // Only send newPassword if it exists
			oldPassword: formData.oldPassword || null, // Only send oldPassword if it's provided
			digitalDescription: formData.digitalDescription,
			serviceCostKs: formData.serviceCostKs,
			serviceCostMk: formData.serviceCostMk,
			serviceCostAl: formData.serviceCostAl,
		};

		setLoading(true);

		try {
			const response = await api.post("User/update-client", data);

			if (response && response.status === 200) {
				toast.success("Successfully updated!");
			} else {
				toast.error("Failed to update profile!");
			}
		} catch (error) {
			console.error("Error updating profile: ", error);
			toast.error("An error occurred while updating the profile.");
		} finally {
			setLoading(false);
			clearData();
		}
	};

	const linkRef = useRef<HTMLInputElement>(null);

	const copyToClipboard = () => {
		if (linkRef.current) {
			linkRef.current.select();
			document.execCommand("copy");
			if (navigator.clipboard) {
				navigator.clipboard.writeText(linkRef.current.value);
			}
			toast.success("TOKEN-i eshte kopjuar!");
		}
	};

	const handleBlur = useCallback(() => {
		if (
			formData.newPassword != null &&
			formData.newPassword.length > 0 &&
			formData.confirmPassword != null &&
			formData.confirmPassword.length > 0
		) {
			if (formData.newPassword !== formData.confirmPassword) {
				message.error("Fjalëkalimet nuk përputhen.");
				setError(true);
			} else {
				message.success("Fjalëkalimet përputhen.");
				setError(false);
			}
		} else {
			setError(false);
			return;
		}
	}, [formData.confirmPassword, formData.newPassword]);

	const getUserDetails = async (id: number) => {
		try {
			const res = await api.get(`User/client/${id}/contract`, {
				responseType: "blob",
			});
			const blob = new Blob([res.data], {
				type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
			});
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `contract-${id}.docx`;
			document.body.appendChild(a);
			a.click();
			a.remove();
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.log(error);
		}
	};

	const handleDownloadContract = () => {
		if (!formData.id) {
			return;
		}

		getUserDetails(formData.id);
	};

	return (
		<>
			<div className="accountContainer p-2">
				<div className="accountMainContainer">
					<div className="profileDiv">
						<h2 className="StatisticTitle">{strings?.myprofile}</h2>
					</div>
					<div className="mainAccountInfo">
						<div className="uploadDiv">
							<Upload className="text-center border-[#D0D0D0] bg-[#EDEDED] border border-dashed w-36 h-36 flex rounded-full  items-center justify-center">
								{strings?.upload} <br /> {strings?.photo}
							</Upload>
						</div>
						<div className="inputDivAccount">
							<div className="firstInfoAccount">
								<CustomInput
									value={formData.nameAndSurname}
									label={strings?.nameAndSurname}
									name="nameAndSurname"
									onChange={handleInputChange}
									className="inputAccount"
									disabled={true}
								/>
								<CustomInput
									value={formData.email}
									label="E-mail"
									name="email"
									onChange={handleInputChange}
									className="inputAccount"
									disabled={true}
								/>
								<CustomInput
									value={formData.username}
									label={strings?.username}
									name="username"
									onChange={handleInputChange}
									className="inputAccount"
									disabled={true}
								/>
								<CustomInput
									value={formData.country}
									label={strings?.country}
									name="country"
									onChange={handleInputChange}
									className="inputAccount"
									disabled={true}
								/>
								<CustomInput
									value={formData.city}
									label={strings?.city}
									name="city"
									onChange={handleInputChange}
									className="inputAccount"
									disabled={true}
								/>
								<CustomInput
									value={formData.serviceCostKs}
									label="Kosto e Shërbimit (Ks)"
									onChange={(e) =>
										setFormData((prevState) => ({
											...prevState,
											serviceCostKs: e.target.value,
										}))
									}
									className="inputAccount"
								/>
								<CustomInput
									value={formData.serviceCostAl}
									label="Kosto e Shërbimit (Al)"
									onChange={(e) =>
										setFormData((prevState) => ({
											...prevState,
											serviceCostAl: e.target.value,
										}))
									}
									className="inputAccount"
								/>
								<CustomInput
									value={formData.serviceCostMk}
									label="Kosto e Shërbimit (Mk)"
									onChange={(e) =>
										setFormData((prevState) => ({
											...prevState,
											serviceCostMk: e.target.value,
										}))
									}
									className="inputAccount"
								/>

								<div className="hidden1"></div>
								<div className="hidden1"></div>
							</div>
							<div className="secondInfoAccount">
								<CustomInput
									value={formData.address}
									label={strings?.address}
									name="address"
									onChange={handleInputChange}
									className="inputAccount"
									disabled={true}
								/>
								<CustomInput
									value={formData.phone}
									label={strings?.phone}
									name="phone"
									onChange={handleInputChange}
									className="inputAccount"
									disabled={true}
								/>
								<DefaultPasswordInput
									name="oldPassword"
									label={strings?.oldpw}
									value={formData.oldPassword}
									onChange={handleInputChange}
									onBlur={handleBlur}
									allowClear={true}
									width={352}
									height={42}
								/>
								<DefaultPasswordInput
									name="newPassword"
									label={strings?.newpw}
									value={formData.newPassword}
									placeholder={strings?.pwdesc}
									onChange={handleInputChange}
									onBlur={handleBlur}
									allowClear={true}
									width={352}
									height={42}
								/>
								<DefaultPasswordInput
									name="confirmPassword"
									label={strings?.confirmpw}
									value={formData.confirmPassword}
									placeholder={strings?.pwdesc}
									onChange={handleInputChange}
									onBlur={handleBlur}
									allowClear={true}
									width={352}
									height={42}
								/>
								<CustomInput
									value={formData.digitalDescription}
									label={strings?.digitalDescription}
									name="digitalDescription"
									onChange={handleInputChange}
									className="inputAccount"
								/>

								{userRole === userRoles.CLIENT && (
									<div className="flex flex-row gap-2">
										<p>
											{
												strings?.checkboxes
													.showNumberInOrder
											}
										</p>
										<Checkbox
											checked={formData.showNumberInOrder}
											onChange={changeShowNumberInOrder}
										/>
									</div>
								)}
							</div>
						</div>
					</div>
					<div className="buttonsAccount">
						<div className="flex sm:flex-col md:flex-col lg:flex-col xl:flex-row xl:w-full gap-5 flex-wrap p-2 justify-between">
							{(userRole === userRoles.CLIENT ||
								userRole === userRoles.SUBCLIENT) && (
								<div className="flex flex-col gap-2">
									<p>TOKEN</p>
									<Space direction="horizontal">
										<input
											type="text"
											readOnly
											value={token || ""}
											className="token"
											ref={linkRef}
										/>
										<Button
											onClick={copyToClipboard}
											className="copy-button"
										>
											{strings?.copy} <CopyOutlined />
										</Button>

										<Button
											className="text-white bg-[#4F16C2] h-11 w-44"
											onClick={handleDownloadContract}
										>
											Download contract
										</Button>
									</Space>
								</div>
							)}

							<div className="flex flex-row justify-end ml-2">
								<Button
									onClick={() => clearData()}
									className="text-[#4F16C2] border-[#4F16C2] h-11 w-36"
								>
									{strings?.backbutton}
								</Button>
								<Button
									className="text-white bg-[#4F16C2] h-11 w-36"
									onClick={updateProfile}
								>
									{strings?.savebutton}
								</Button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default MyAccount;
